import type { KycLevel, KycStatus } from "../types/identities-api.types";

/**
 * UI tier for an identity, derived from the backend's `kyc_level`. NN → express (gris), BASIC → verified
 * (verde), ADVANCED → pro (violeta).
 */
export type IdentityTier = "express" | "verified" | "pro";

/**
 * Tier presentation data — `maxCards`, `monthCap` and `perks` are UI defaults (no backend equivalent yet).
 * They paint the tier progress card in the detail view; the moment we get limits-by-tier from the API these
 * become the fallback only.
 */
export type IdentityTierInfo = {
  id: IdentityTier;
  /** Display label (key in the `cards` i18n namespace). */
  labelKey: string;
  /** Solid hex color used by the avatar gradient + chip dot. */
  color: string;
  /** Max number of cards. Use `Infinity` to represent "ilimitado". */
  maxCards: number;
  /** Monthly USD spend cap. Use `Infinity` for "sin tope". */
  monthCap: number;
  /** Translation keys (in `cards` namespace) describing the perks. */
  perksKeys: ReadonlyArray<string>;
};

/**
 * Note: the numeric limits below are UI placeholders. The backend does not expose limits-by-tier today, so
 * changing a tier in the DB does not move these values. When the API ships per-tier limits we'll read them
 * from the org config and treat this constant as the fallback.
 */
export const TIER_INFO: Record<IdentityTier, IdentityTierInfo> = {
  express: {
    id: "express",
    labelKey: "identities_tier_express",
    color: "#94a3b8",
    maxCards: 1,
    monthCap: 500,
    perksKeys: ["identities_tier_express_perk_one_card", "identities_tier_express_perk_monthly_cap"],
  },
  verified: {
    id: "verified",
    labelKey: "identities_tier_verified",
    color: "#0d8848",
    maxCards: 5,
    monthCap: 5000,
    perksKeys: [
      "identities_tier_verified_perk_unlimited_virtual",
      "identities_tier_verified_perk_monthly_cap",
      "identities_tier_verified_perk_higher_topups",
    ],
  },
  pro: {
    id: "pro",
    labelKey: "identities_tier_pro",
    color: "#7c3aed",
    maxCards: Infinity,
    monthCap: Infinity,
    perksKeys: [
      "identities_tier_pro_perk_physical",
      "identities_tier_pro_perk_no_cap",
      "identities_tier_pro_perk_funds_origin",
    ],
  },
};

export const TIER_ORDER: ReadonlyArray<IdentityTier> = ["express", "verified", "pro"];

/**
 * Maps the backend `kyc_level` enum to the UI tier. Any unknown value (including NN / null) falls back to
 * `express` so the UI always has something to render.
 */
export const kycLevelToTier = (level: KycLevel | string | null | undefined): IdentityTier => {
  if (!level) return "express";
  const normalized = String(level).toUpperCase();
  if (normalized === "ADVANCED" || normalized === "FULL") return "pro";
  if (normalized === "BASIC" || normalized === "MEDIUM") return "verified";
  return "express";
};

/**
 * KYC status presentation. The backend has a small enum (`PENDING`, `IN_REVIEW`, `VERIFIED`, `REJECTED`,
 * `EXPIRED`); the design groups everything that is not VERIFIED/REJECTED into "pendiente".
 */
export type KycStatusTone = "success" | "warning" | "destructive" | "secondary";

export type KycStatusMeta = {
  /** Tone the design system Badge should use. */
  tone: KycStatusTone;
  /** Translation key (in `cards` namespace). */
  labelKey: string;
  /** Group used by the listing filter chip. */
  group: "approved" | "pending" | "rejected";
};

export const kycStatusMeta = (status: KycStatus | string | null | undefined): KycStatusMeta => {
  if (!status) {
    return { tone: "warning", labelKey: "identities_kyc_pending", group: "pending" };
  }
  const normalized = String(status).toUpperCase();
  if (normalized === "VERIFIED") {
    return { tone: "success", labelKey: "identities_kyc_approved", group: "approved" };
  }
  if (normalized === "REJECTED" || normalized === "EXPIRED") {
    return { tone: "destructive", labelKey: "identities_kyc_rejected", group: "rejected" };
  }
  return { tone: "warning", labelKey: "identities_kyc_pending", group: "pending" };
};

/**
 * Display name resolver — picks the best human name from an identity record. Falls back to email, then to a
 * placeholder if nothing is present.
 */
export const resolveIdentityDisplayName = (identity: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  metadata?: { custom_fields?: Record<string, unknown> } | null;
  organization?: { name?: string | null } | null;
}): string => {
  const first = (identity.first_name ?? "").trim();
  const last = (identity.last_name ?? "").trim();
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  const businessName = (identity.metadata?.custom_fields?.business_name as string | undefined) ?? null;
  if (businessName?.trim()) return businessName.trim();
  if (identity.organization?.name?.trim()) return identity.organization.name.trim();
  return identity.email?.trim() ?? "";
};

/**
 * Initials for the avatar — first letter of name + first of last name; or the first two letters of whatever
 * name we managed to resolve.
 */
export const resolveIdentityInitials = (displayName: string): string => {
  if (!displayName) return "?";
  return (
    displayName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
};
